import React, { ReactElement, Ref } from 'react';

import PropTypes from 'prop-types';

import { Control, ControlProps, Placeholder, SearchInput } from '../layout';
import { GenericItem, GetInputProps } from '../types';

export type AutoCompleteControlProps<TItem extends GenericItem> =
  ControlProps<TItem> &
    GetInputProps & {
      openMenu: () => void;
    };

const AutocompleteControlWrapped = <TItem extends GenericItem>(
  {
    isOpen,
    getInputProps,
    openMenu,
    disabled,
    ...props
  }: AutoCompleteControlProps<TItem>,
  ref: Ref<HTMLDivElement>,
) => {
  const {
    style: inputStyle,
    css: _ignored,
    ...inputProps
  } = getInputProps({
    readOnly: !isOpen,
  });

  return (
    <Control
      ref={ref}
      isOpen={isOpen}
      renderInput={() => {
        return (
          <SearchInput
            style={{ ...inputStyle }}
            isOpen={isOpen}
            disabled={disabled}
            {...inputProps}
          />
        );
      }}
      renderItem={isOpen ? () => null : undefined} // undefined will use the default
      renderPlaceholder={(placeholderProps) => {
        return isOpen ? null : <Placeholder {...placeholderProps} />;
      }}
      onClick={() => {
        !isOpen && !disabled && openMenu();
      }}
      disabled={disabled}
      isAutocomplete
      {...props}
    />
  );
};

const AutocompleteControl = React.forwardRef(AutocompleteControlWrapped);

AutocompleteControl.displayName = 'AutocompleteControl';

AutocompleteControl.propTypes = {
  /** Function to open menu.  Provided by useAutocomplete hook */
  openMenu: PropTypes.func.isRequired,
};

export default AutocompleteControl as <T extends GenericItem>(
  p: AutoCompleteControlProps<T> & {
    ref?: Ref<HTMLDivElement | HTMLLabelElement>;
  },
) => ReactElement;
